import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { BrandLink } from "@/components/Brand";
import { TERMS_VERSION } from "@/pages/TermsPage";

export default function RegisterPage() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", workshop_name: "", email: "", password: "" });
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!termsAccepted) {
      toast.error("Please accept the Terms of Service to continue.");
      return;
    }
    setBusy(true);
    try {
      await register({ ...form, terms_accepted: true, terms_version: TERMS_VERSION });
      toast.success("Account created");
      nav("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Registration failed");
    } finally { setBusy(false); }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="min-h-screen grid md:grid-cols-2">
      <div className="flex items-center justify-center p-6 md:p-12 bg-background order-2 md:order-1">
        <form onSubmit={submit} className="w-full max-w-md space-y-5" data-testid="register-form">
          <div>
            <div className="label-overline">Create account</div>
            <h1 className="font-display text-3xl font-bold tracking-tight mt-2">Open your workshop</h1>
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium">Your name</label>
            <input required value={form.name} onChange={set("name")} className="input-default" data-testid="register-name-input" />
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium">Workshop name</label>
            <input required value={form.workshop_name} onChange={set("workshop_name")} className="input-default" data-testid="register-workshop-input" />
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium">Email</label>
            <input type="email" required value={form.email} onChange={set("email")} className="input-default" data-testid="register-email-input" />
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium">Password</label>
            <input type="password" minLength={6} required value={form.password} onChange={set("password")} className="input-default" data-testid="register-password-input" />
            <p className="text-xs text-muted-foreground">At least 6 characters.</p>
          </div>
          <label className="flex items-start gap-2.5 cursor-pointer select-none rounded-lg border border-border p-3 hover:bg-muted/40 transition-colors" data-testid="register-terms-label">
            <input
              type="checkbox"
              checked={termsAccepted}
              onChange={(e) => setTermsAccepted(e.target.checked)}
              className="mt-0.5 w-4 h-4 accent-primary cursor-pointer"
              data-testid="register-terms-checkbox"
            />
            <span className="text-xs leading-relaxed text-muted-foreground">
              I agree to the{" "}
              <Link to="/terms" target="_blank" rel="noreferrer" className="text-primary font-semibold hover:underline" data-testid="register-terms-link">
                Terms of Service
              </Link>{" "}
              (including the <strong className="text-foreground">onboarding fee (TBA post-pilot)</strong> and your choice of{" "}
              <strong className="text-foreground">Option A (flat SaaS) or Option B (commission on approved extras)</strong>{" "}
              on a 12-month minimum term) and the{" "}
              <Link to="/privacy" target="_blank" rel="noreferrer" className="text-primary font-semibold hover:underline" data-testid="register-privacy-link">
                Privacy Policy
              </Link>.
            </span>
          </label>
          <button type="submit" disabled={busy || !termsAccepted} className="btn-primary w-full" data-testid="register-submit-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Create account <ArrowRight className="w-4 h-4" /></>}
          </button>
          <p className="text-sm text-muted-foreground">
            Already have one? <Link to="/login" className="text-primary font-semibold hover:underline" data-testid="register-to-login-link">Sign in</Link>
          </p>
        </form>
      </div>

      <div className="hidden md:block relative overflow-hidden order-1 md:order-2">
        <img src="https://images.unsplash.com/photo-1727893119356-1702fe921cf9?auto=format&fit=crop&w=1400&q=80" alt="Workshop" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/55" />
        <div className="relative h-full p-12 flex flex-col justify-between text-white">
          <div className="bg-card rounded-lg p-3 w-fit"><BrandLink /></div>
          <div>
            <div className="label-overline text-white/60">Why workshops choose ServiceSell Pro</div>
            <h2 className="font-display text-4xl font-bold tracking-tight mt-3 leading-tight">
              Your tools. Your video.<br /><span className="text-primary">More yes.</span>
            </h2>
          </div>
          <div className="font-mono-plex text-xs text-white/50">No card required to start</div>
        </div>
      </div>
    </div>
  );
}
